import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE } from "./site";

type Props = {
  title?: string;
  description?: string;
  image?: string; // absolute or relative
  url?: string; // path or absolute
  type?: "website" | "article";
  noIndex?: boolean;
  jsonLd?: Record<string, any>[];
  keywords?: string[];
  author?: string;
  publisher?: string;
};

/* -------------------- helpers -------------------- */

function cleanPath(p?: string) {
  if (!p) return "/";
  const noQueryHash = p.split("?")[0].split("#")[0];
  if (!noQueryHash) return "/";
  if (noQueryHash !== "/" && noQueryHash.endsWith("/"))
    return noQueryHash.slice(0, -1);
  return noQueryHash;
}

function toAbsolute(u?: string) {
  if (!u) return undefined;
  const trimmed = u.trim();
  if (!trimmed) return undefined;

  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  const domain = SITE.domain.replace(/\/+$/, "");
  const path = cleanPath(trimmed.startsWith("/") ? trimmed : `/${trimmed}`);
  return `${domain}${path}`;
}

/* -------------------- component -------------------- */

export default function Seo({
  title,
  description,
  image,
  url,
  type = "website",
  noIndex = false,
  jsonLd = [],
  keywords,
  author,
  publisher,
}: Props) {
  const location = useLocation();

  /* -------- Core identity -------- */
  const metaTitle = title || SITE.name;

  const baseDesc = description || SITE.description;
  const metaDesc =
    baseDesc.length > 155 ? `${baseDesc.slice(0, 152)}...` : baseDesc;

  const metaKeywords =
    keywords && keywords.length > 0 ? keywords.join(", ") : undefined;

  const metaAuthor = author || "Team Babblebots";
  const metaPublisher = publisher || SITE.name;

  /* -------- Canonical (CRITICAL FIX) -------- */
  const fallbackPath = cleanPath(location.pathname || "/");
  const canonicalUrl = toAbsolute(url ?? fallbackPath);

  /* -------- OG / Twitter -------- */
  const ogImage = toAbsolute(image) || toAbsolute(SITE.ogDefault);
  const ogUrl = canonicalUrl;

  /* -------- Base JSON-LD (LLM-safe) -------- */
  const baseJsonLd: Record<string, any>[] = [
    {
      "@context": "https://schema.org",
      "@type": type === "article" ? "Article" : "WebPage",
      name: metaTitle,
      description: metaDesc,
      url: canonicalUrl,
      author: {
        "@type": "Organization",
        name: metaAuthor,
      },
      publisher: {
        "@type": "Organization",
        name: metaPublisher,
        url: SITE.domain,
        logo: {
          "@type": "ImageObject",
          url: toAbsolute(SITE.logo),
        },
      },
    },
  ];

  return (
    <Helmet>
      {/* ================= BASIC ================= */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />

      {metaKeywords && <meta name="keywords" content={metaKeywords} />}
      <meta name="author" content={metaAuthor} />
      <meta name="publisher" content={metaPublisher} />

      <meta
        name="robots"
        content={noIndex ? "noindex, nofollow" : "index, follow"}
      />

      {/* ================= CANONICAL ================= */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* ================= OPEN GRAPH ================= */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}

      {/* ================= TWITTER ================= */}
      <meta name="twitter:card" content="summary_large_image" />
      {SITE.twitter && (
        <meta name="twitter:site" content={SITE.twitter} />
      )}
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* ================= JSON-LD ================= */}
      <script type="application/ld+json">
        {JSON.stringify([...baseJsonLd, ...jsonLd])}
      </script>
    </Helmet>
  );
}
